import { OnboardingStyles } from "features/onboarding/common/styles";
import React, { useContext } from "react";
import Col from "antd/es/col";
import Row from "antd/es/row";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH, SMALL_SCREEN, TABLET } from "common/constants";
import ConnectExchangeExtraInfo from "features/onboarding/connectExchange/ConnectExchangeExtraInfo";
import { OnboardingDetailedStep } from "@shared/features/onboarding/types";
import {
  CONNECT_EXCHANGE_PRIMARY_TITLE,
  DEMO_MODE_CONNECT_EXCHANGE_PRIMARY_TITLE,
} from "@shared/features/onboarding/constants";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import useQuery from "utils/hooks/useQuery";
import ConnectExchange from "features/portfolio/CreateAccount/ConnectExchange";
import { QuickConnectContext } from "@shared/features/onboarding/QuickConnect.context";
import { ConnectExchangeTabs } from "@shared/features/portfolio/types";
import { AddClientResponse } from "@shared/types";
import styled, { useTheme } from "styled-components";
import ChangeAccountTypeMessage from "features/configuration/accounts/CreateAccount/ChangeAccountTypeMessage.tsx";
import Flex from "common/Boxes/Flex.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import { ConnectExternalAccountStep } from "@shared/features/configuration/types.ts";

interface ConnectExchangeProps {
  getErrorNotification: (
    description: string,
    title?: string | undefined
  ) => void;
  getSuccessMessage?: (
    response?: AddClientResponse,
    exchangesList?: string[]
  ) => void;
}

const OnboardingConnectExchange = ({
  getErrorNotification,
  getSuccessMessage,
}: ConnectExchangeProps) => {
  const theme = useTheme();
  const { changeStep } = useContext(OnboardingContext) || {};
  const { isExternalConnectAccount, setStep } =
    useContext(ExternalConnectAccountContext) || {};
  const { isDemoWorkspace, tab, exchangesLoading } =
    useContext(AccountFormContext)!;
  const { quickConnectLoading } = useContext(QuickConnectContext) || {};
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const isTablet = useMedia([`(max-width: ${TABLET})`], [true], false);
  const isSmallScreen = useMedia(
    [`(max-width: ${SMALL_SCREEN})`],
    [true],
    false
  );
  const query = useQuery();
  const primaryExchange = query.get("exchange") || undefined;
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();

  const connectExchange = (
    <ConnectExchange
      primaryExchange={primaryExchange}
      isOnboarding={true}
      onSubmitSuccess={() => {
        if (isExternalConnectAccount) {
          setStep?.(ConnectExternalAccountStep.SUCCESS);
        } else {
          changeStep?.(OnboardingDetailedStep.TRADING_METHOD);
        }
      }}
      getErrorNotification={getErrorNotification}
      getSuccessMessage={getSuccessMessage}
    />
  );

  const extraInfo = (
    <ConnectExchangeExtraInfo
      primaryExchange={primaryExchange}
      isQuickConnect={tab === ConnectExchangeTabs.QUICK_CONNECT}
    />
  );

  return (
    <OnboardingStyles.StepWrapper
      $isMobile={isMobile}
      style={{ width: "100%", maxWidth: 1000 }}
    >
      <LoadingSpinnerOverlay
        isLoading={quickConnectLoading || !primaryExchange || exchangesLoading}
        hideContentWhileLoading={!primaryExchange || exchangesLoading}
        backgroundColor={theme.colors.layoutBgColor}
        wrapStyle={{ width: "100%" }}
        wrap={true}
      >
        {isTablet ? (
          <MobileWrapper>
            <ContentWrapper
              $isBottomNavigator={
                isBottomNavigator && !isExternalConnectAccount
              }
            >
              {connectExchange}
              {extraInfo}
            </ContentWrapper>
            {isBottomNavigator && !isExternalConnectAccount && (
              <MessageWrapper>
                <ChangeAccountTypeMessage />
              </MessageWrapper>
            )}
          </MobileWrapper>
        ) : (
          <>
            <OnboardingStyles.Title style={{ marginBottom: 48 }}>
              {isDemoWorkspace
                ? DEMO_MODE_CONNECT_EXCHANGE_PRIMARY_TITLE
                : CONNECT_EXCHANGE_PRIMARY_TITLE}
            </OnboardingStyles.Title>
            <Row gutter={isSmallScreen ? 24 : 64}>
              {primaryExchange && (
                <Col span={isSmallScreen ? 11 : 12}>{extraInfo}</Col>
              )}
              <Col span={primaryExchange ? (isSmallScreen ? 13 : 12) : 24}>
                {connectExchange}
              </Col>
            </Row>
          </>
        )}
      </LoadingSpinnerOverlay>
    </OnboardingStyles.StepWrapper>
  );
};

export default OnboardingConnectExchange;

const MobileWrapper = styled(Flex)`
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  height: 100%;
`;
const ContentWrapper = styled.div<{ $isBottomNavigator?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
  height: 100%;
  width: 100%;
  ${(p) =>
    p.$isBottomNavigator &&
    `
  padding: 0 16px;
  `};
`;
const MessageWrapper = styled.div<{ $isBottomNavigator?: boolean }>`
  padding: 16px 16px 0;
  width: 100%;
`;
